import React, { useRef, useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocTitle } from '../../hooks/useDocTitle';
import logoWhite from '../../assets/logo_white_transparent.png';

/* ── keyframes ──────────────────────────────── */
const gridAnim   = keyframes`from{background-position:0 0}to{background-position:0 60px}`;
const fadeUp     = keyframes`from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}`;
const fallDown   = keyframes`from{transform:translateY(-20px) rotate(0);opacity:1}to{transform:translateY(110vh) rotate(720deg);opacity:0}`;
const shakeAnim  = keyframes`0%,100%{transform:none}20%{transform:translateX(-8px)}60%{transform:translateX(8px)}`;
const emojiIn    = keyframes`0%{transform:scale(0) rotate(-10deg)}70%{transform:scale(1.15)}100%{transform:scale(1)}`;
const matchGlow  = keyframes`0%,100%{box-shadow:0 0 8px rgba(0,255,136,.3)}50%{box-shadow:0 0 28px rgba(0,255,136,.8)}`;
const timerWarn  = keyframes`0%,100%{opacity:1}50%{opacity:.3}`;
const previewFade= keyframes`0%{opacity:0}20%{opacity:1}80%{opacity:1}100%{opacity:0}`;

/* ── layout ─────────────────────────────────── */
const Page = styled.div`
  min-height:100vh;background:#02020E;
  display:flex;flex-direction:column;position:relative;overflow:hidden;
`;
const GridBg = styled.div`
  position:fixed;inset:0;pointer-events:none;z-index:0;
  background-image:
    linear-gradient(rgba(200,0,255,.025) 1px,transparent 1px),
    linear-gradient(90deg,rgba(200,0,255,.025) 1px,transparent 1px);
  background-size:60px 60px;animation:${gridAnim} 18s linear infinite;
`;
const GlowBg = styled.div`
  position:fixed;width:min(600px,100vw);height:min(600px,100vw);border-radius:50%;
  left:50%;top:38%;transform:translate(-50%,-50%);
  background:radial-gradient(circle,rgba(200,0,255,.07) 0%,transparent 70%);
  pointer-events:none;z-index:0;
`;
const Piece = styled.div`
  position:fixed;pointer-events:none;z-index:200;
  width:${p=>p.$w}px;height:${p=>p.$h}px;background:${p=>p.$c};
  left:${p=>p.$x}%;top:-20px;border-radius:${p=>p.$r?'50%':'2px'};
  animation:${fallDown} ${p=>p.$d}s ease ${p=>p.$dl}s both;
`;

/* ── preview overlay ────────────────────────── */
const PreviewBanner = styled.div`
  position:fixed;top:80px;left:50%;transform:translateX(-50%);
  z-index:300;background:rgba(200,0,255,.9);padding:8px 28px;
  font-family:'Orbitron',monospace;font-size:12px;font-weight:700;
  letter-spacing:.22em;color:#fff;text-transform:uppercase;
  clip-path:polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%);
  animation:${previewFade} ${p=>p.$dur}s ease forwards;
  pointer-events:none;
`;

/* ── topbar ──────────────────────────────────── */
const TopBar = styled.header`
  position:relative;z-index:10;
  display:flex;align-items:center;justify-content:space-between;
  padding:14px 48px;border-bottom:1px solid rgba(200,0,255,.12);
  background:rgba(2,2,14,.96);backdrop-filter:blur(14px);
  @media(max-width:560px){padding:12px 20px}
`;
const BackBtn = styled(Link)`
  display:flex;align-items:center;gap:6px;
  font-family:'Space Mono',monospace;font-size:10px;letter-spacing:.22em;
  color:rgba(255,255,255,.4);text-decoration:none;text-transform:uppercase;
  transition:color .25s;&:hover{color:#C800FF}
`;
const LogoLink = styled(Link)`
  display:flex;align-items:center;gap:8px;text-decoration:none;
  img{height:32px;object-fit:contain;display:block;}
`;
const Chip = styled.span`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.28em;
  color:rgba(200,0,255,.5);border:1px solid rgba(200,0,255,.15);
  padding:4px 12px;text-transform:uppercase;
  @media(max-width:560px){display:none}
`;

/* ── title ───────────────────────────────────── */
const TitleArea = styled.div`
  text-align:center;padding:20px 20px 2px;position:relative;z-index:2;
  animation:${fadeUp} .5s ease both;
`;
const H1 = styled.h1`
  font-family:'Bebas Neue',sans-serif;font-size:clamp(46px,10vw,88px);line-height:.88;
  background:linear-gradient(135deg,#fff 20%,#C800FF 100%);
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;letter-spacing:.04em;
`;
const Sub = styled.p`
  font-family:'Rajdhani',sans-serif;font-size:13px;letter-spacing:.24em;
  color:rgba(255,255,255,.25);text-transform:uppercase;margin-top:4px;
`;

/* ── HUD ─────────────────────────────────────── */
const HUD = styled.div`
  position:relative;z-index:2;
  display:flex;align-items:center;justify-content:center;
  gap:clamp(16px,4vw,56px);padding:16px 24px 6px;flex-wrap:wrap;
`;
const Stat   = styled.div`text-align:center`;
const SLabel = styled.div`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.26em;
  color:rgba(255,255,255,.28);text-transform:uppercase;margin-bottom:3px;
`;
const SVal = styled.div`
  font-family:'Bebas Neue',sans-serif;font-size:clamp(28px,5vw,44px);line-height:1;
  background:${p=>p.$g||'linear-gradient(135deg,#C800FF,#00A3FF)'};
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;
`;
const TimerBox = styled.div`position:relative;width:52px;height:52px`;
const TimerNum = styled.div`
  position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
  font-family:'Orbitron',monospace;font-size:13px;font-weight:700;
  color:${p=>p.$w?'#FF3366':'#C800FF'};
  ${p=>p.$w&&css`animation:${timerWarn} .7s ease infinite`}
`;

/* ── controls ────────────────────────────────── */
const Controls = styled.div`
  position:relative;z-index:2;
  display:flex;align-items:center;justify-content:center;
  gap:7px;padding:6px 24px 10px;flex-wrap:wrap;
`;
const DBtn = styled.button`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.18em;text-transform:uppercase;
  padding:7px 15px;border:1px solid ${p=>p.$a?'#C800FF':'rgba(255,255,255,.1)'};
  background:${p=>p.$a?'rgba(200,0,255,.1)':'transparent'};
  color:${p=>p.$a?'#C800FF':'rgba(255,255,255,.35)'};
  clip-path:polygon(6px 0%,100% 0%,calc(100% - 6px) 100%,0% 100%);
  transition:all .3s;opacity:${p=>p.disabled?.45:1};
  &:hover:not(:disabled){border-color:#C800FF;color:#C800FF}
`;
const StartBtn = styled.button`
  font-family:'Rajdhani',sans-serif;font-weight:700;font-size:13px;
  letter-spacing:.2em;text-transform:uppercase;padding:9px 28px;
  background:linear-gradient(135deg,#C800FF,#6B00FF);border:none;color:#fff;
  clip-path:polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%);
  transition:all .3s;&:hover{box-shadow:0 0 22px rgba(200,0,255,.45);transform:translateY(-2px)}
`;

/* ── board ───────────────────────────────────── */
const BoardArea = styled.div`
  position:relative;z-index:2;flex:1;
  display:flex;justify-content:center;align-items:flex-start;
  padding:4px 16px 36px;
`;
const Grid = styled.div`
  display:grid;
  grid-template-columns:repeat(${p=>p.$c},1fr);
  gap:${p=>p.$c>4?'7px':'9px'};width:100%;
  max-width:${p=>p.$c===3?'340px':p.$c===4?'480px':'660px'};
`;

/*
  CARD ARCHITECTURE — dead simple, zero browser bugs:
  CardBack and CardFront are both absolute, inset:0.
  Exactly ONE has display:flex at any time (the other has display:none).
  No 3D, no opacity transitions, no backface-visibility.
*/
const CardOuter = styled.div`
  aspect-ratio:1;position:relative;cursor:pointer;
  ${p=>p.$shake&&css`animation:${shakeAnim} .45s ease`}
`;
const CardBack = styled.div`
  position:absolute;inset:0;border-radius:4px;
  display:${p=>p.$v?'flex':'none'};
  align-items:center;justify-content:center;
  background:rgba(10,8,28,.96);border:1px solid rgba(200,0,255,.22);overflow:hidden;
  &::before{
    content:'';position:absolute;inset:0;pointer-events:none;
    background:repeating-linear-gradient(45deg,rgba(200,0,255,.04) 0,rgba(200,0,255,.04) 1px,transparent 1px,transparent 13px);
  }
  &::after{
    content:'G';position:absolute;
    font-family:'Orbitron',monospace;font-size:clamp(16px,3.5vw,28px);font-weight:900;
    color:rgba(200,0,255,.14);pointer-events:none;
  }
`;
const CardFront = styled.div`
  position:absolute;inset:0;border-radius:4px;
  display:${p=>p.$v?'flex':'none'};
  align-items:center;justify-content:center;font-size:clamp(18px,5vw,36px);
  background:${p=>p.$m
    ?'linear-gradient(135deg,rgba(0,255,136,.13),rgba(0,163,255,.1))'
    :'linear-gradient(135deg,rgba(200,0,255,.13),rgba(107,0,255,.09))'};
  border:1px solid ${p=>p.$m?'rgba(0,255,136,.55)':'rgba(200,0,255,.4)'};
  ${p=>p.$m&&css`animation:${matchGlow} 2s ease infinite`};
`;
const EmojiSpan = styled.span`
  display:inline-block;
  animation:${emojiIn} .22s cubic-bezier(.4,0,.2,1) both;
`;

/* ── modal ───────────────────────────────────── */
const Overlay = styled.div`
  position:fixed;inset:0;z-index:100;
  background:rgba(2,2,14,.92);backdrop-filter:blur(8px);
  display:flex;align-items:center;justify-content:center;
`;
const Modal = styled.div`
  text-align:center;padding:50px 42px;
  border:1px solid rgba(200,0,255,.25);background:rgba(8,6,24,.98);
  max-width:370px;width:90%;position:relative;overflow:hidden;
  animation:${fadeUp} .4s ease;
  &::before{content:'';position:absolute;top:0;left:0;width:3px;height:100%;
    background:linear-gradient(to bottom,#C800FF,#00A3FF)}
`;
const MEmo   = styled.div`font-size:56px;margin-bottom:12px`;
const MTit   = styled.h2`font-family:'Bebas Neue',sans-serif;font-size:46px;letter-spacing:.04em;color:#fff;margin-bottom:8px`;
const MSub   = styled.p`font-size:14px;color:rgba(255,255,255,.42);line-height:1.6;margin-bottom:10px`;
const MScore = styled.div`
  font-family:'Orbitron',monospace;font-size:28px;font-weight:700;
  background:linear-gradient(135deg,#C800FF,#00A3FF);
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;margin:12px 0;
`;
const MBtn = styled.button`
  font-family:'Rajdhani',sans-serif;font-weight:700;font-size:13px;
  letter-spacing:.2em;text-transform:uppercase;padding:12px 30px;
  background:linear-gradient(135deg,#C800FF,#6B00FF);border:none;color:#fff;
  clip-path:polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%);
  transition:all .3s;&:hover{box-shadow:0 0 22px rgba(200,0,255,.5)}
`;
const Empty = styled.div`
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  gap:14px;padding:60px 20px;font-family:'Rajdhani',sans-serif;font-size:15px;
  letter-spacing:.2em;color:rgba(255,255,255,.15);text-transform:uppercase;text-align:center;
`;

/* ── game data ───────────────────────────────── */
const DIFFS = {
  fácil:   {pairs:6,  cols:3, secs:90,  preview:4000},
  médio:   {pairs:8,  cols:4, secs:60,  preview:3000},
  difícil: {pairs:12, cols:6, secs:45,  preview:2000},
};
const EMOJIS = ['🎮','🚀','🌌','⚡','🔮','💎','🎯','🔥','🌊','👾','🎲','🏆'];
const CFCOLORS = ['#FF3366','#00A3FF','#C800FF','#FFB800','#00FF88','#FF6B00'];

function shuffle(a){const b=[...a];for(let i=b.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[b[i],b[j]]=[b[j],b[i]];}return b;}

function buildDeck(n){
  const emojis=shuffle(EMOJIS).slice(0,n);
  // ALL cards start face-down (open:false)
  return shuffle([...emojis,...emojis].map((emoji,i)=>({id:i,emoji,open:false,matched:false})));
}

function makeConfetti(){
  return Array.from({length:55},(_,i)=>({
    id:i,x:Math.random()*100,w:6+Math.random()*8,h:6+Math.random()*14,
    c:CFCOLORS[i%CFCOLORS.length],d:1.8+Math.random()*2,dl:Math.random()*1.2,r:Math.random()>.5,
  }));
}

/* ── component ───────────────────────────────── */
/*
  STATE PATTERN:
  - G (useRef) holds ALL mutable game state. Never stale.
  - rerender() is the only React state — just a counter to force re-render.
  - No useState for game logic = zero stale closure bugs.

  MEMORY GAME MECHANIC:
  - On start: show all cards (open:true) for 'preview' ms so player can memorize
  - After preview: close all cards (open:false), timer starts
  - Player clicks two cards; if match → stay open; if no match → close after 900ms
*/
export default function MemoryGame(){
  const { t } = useTranslation();
  useDocTitle('pageTitles.memory');
  const timerRef   = useRef(null);
  const previewRef = useRef(null);
  const [,setR]    = useState(0);
  const rerender   = useCallback(()=>setR(n=>n+1),[]);
  const [confetti, setConfetti] = useState([]);
  const [preview,  setPreview]  = useState(false); // banner visible

  // Single source of truth — mutate, then rerender()
  const G = useRef({
    phase:    'idle',  // idle | preview | play | won | lost
    diff:     'médio',
    cards:    [],
    firstCard: null,
    locked:   false,
    pairsFound: 0,
    moves:    0,
    secs:     60,
    totalSecs: 60,
    shakeIds: [],
  }).current;

  // ── start ──
  const start = ()=>{
    clearInterval(timerRef.current);
    clearTimeout(previewRef.current);
    const cfg = DIFFS[G.diff];

    G.phase     = 'preview';
    G.cards     = buildDeck(cfg.pairs);
    G.firstCard = null;
    G.locked    = true; // locked during preview
    G.pairsFound= 0;
    G.moves     = 0;
    G.secs      = cfg.secs;
    G.totalSecs = cfg.secs;
    G.shakeIds  = [];

    // Open ALL cards so player can see them
    G.cards.forEach(c=>{ c.open=true; });
    setPreview(true);
    setConfetti([]);
    rerender();

    // After preview duration, close all and start timer
    previewRef.current = setTimeout(()=>{
      G.cards.forEach(c=>{ c.open=false; });
      G.phase  = 'play';
      G.locked = false;
      setPreview(false);
      rerender();

      timerRef.current = setInterval(()=>{
        if(G.phase!=='play'){clearInterval(timerRef.current);return;}
        G.secs--;
        if(G.secs<=0){G.phase='lost';clearInterval(timerRef.current);}
        rerender();
      },1000);
    }, cfg.preview);
  };

  // ── click ──
  const clickCard = (id)=>{
    if(G.phase!=='play'||G.locked) return;
    const card=G.cards.find(c=>c.id===id);
    if(!card||card.open||card.matched) return;

    card.open=true;
    rerender();

    if(!G.firstCard){ G.firstCard=card; return; }

    const first=G.firstCard;
    G.firstCard=null;
    G.locked=true;
    G.moves++;

    if(first.emoji===card.emoji){
      // ✅ match
      first.matched=card.matched=true;
      G.locked=false;
      G.pairsFound++;
      if(G.pairsFound===DIFFS[G.diff].pairs){
        G.phase='won';
        clearInterval(timerRef.current);
        setTimeout(()=>setConfetti(makeConfetti()),200);
      }
      rerender();
    } else {
      // ❌ no match — shake and close after delay
      G.shakeIds=[first.id,card.id];
      rerender();
      setTimeout(()=>{
        first.open=card.open=false;
        G.shakeIds=[];
        G.locked=false;
        rerender();
      },900);
    }
  };

  const pct  = G.secs/(G.totalSecs||60);
  const circ = 204;
  const score= Math.max(0,G.pairsFound*100+G.secs*5-G.moves*2);
  const cfg  = DIFFS[G.diff];

  return(
    <Page>
      <GridBg/><GlowBg/>
      {confetti.map(c=><Piece key={c.id} $x={c.x} $w={c.w} $h={c.h} $c={c.c} $d={c.d} $dl={c.dl} $r={c.r}/>)}
      {preview && <PreviewBanner $dur={cfg.preview/1000}>{t('games.memory.previewBanner', { seconds: cfg.preview/1000 })}</PreviewBanner>}

      <TopBar>
        <BackBtn to="/games">← {t('games.backToGames')}</BackBtn>
        <LogoLink to="/"><img src={logoWhite} alt="Guebly Games"/></LogoLink>
        <Chip>Memory</Chip>
      </TopBar>

      <TitleArea><H1>MEMORY</H1><Sub>{t('games.memory.subtitle')}</Sub></TitleArea>

      <HUD>
        <Stat><SLabel>{t('games.memory.labelPairs')}</SLabel><SVal>{G.pairsFound}/{cfg.pairs}</SVal></Stat>
        <Stat><SLabel>{t('games.memory.labelMoves')}</SLabel><SVal $g="linear-gradient(135deg,#C800FF,#6B00FF)">{G.moves}</SVal></Stat>
        <Stat>
          <SLabel>{t('games.memory.labelTime')}</SLabel>
          <TimerBox>
            <svg width="52" height="52" viewBox="0 0 76 76" style={{transform:'rotate(-90deg)'}}>
              <circle cx="38" cy="38" r="32.5" fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="5"/>
              <circle cx="38" cy="38" r="32.5" fill="none"
                stroke={G.secs<15?'#FF3366':'#C800FF'} strokeWidth="5" strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={circ*(1-Math.max(0,pct))}
                style={{transition:'stroke-dashoffset 1s linear,stroke .3s'}}
              />
            </svg>
            <TimerNum $w={G.secs<15}>{G.secs}</TimerNum>
          </TimerBox>
        </Stat>
        <Stat><SLabel>{t('games.memory.labelScore')}</SLabel><SVal $g="linear-gradient(135deg,#FFB800,#FF6B00)">{G.phase==='idle'?0:score}</SVal></Stat>
      </HUD>

      <Controls>
        {Object.keys(DIFFS).map(d=>(
          <DBtn key={d} $a={G.diff===d} disabled={G.phase==='preview'||G.phase==='play'}
            onClick={()=>{if(G.phase!=='preview'&&G.phase!=='play'){G.diff=d;G.phase='idle';G.cards=[];rerender();}}}>
            {d === 'fácil' ? t('games.memory.diffEasy') : d === 'médio' ? t('games.memory.diffMedium') : t('games.memory.diffHard')}
          </DBtn>
        ))}
        <StartBtn onClick={start} disabled={G.phase==='preview'}>
          {G.phase==='idle' ? t('games.memory.btnStart') : G.phase==='preview' ? t('games.memory.btnMemorizing') : t('games.memory.btnRestart')}
        </StartBtn>
      </Controls>

      <BoardArea>
        {G.cards.length===0
          ?<Empty><span style={{fontSize:52}}>🃏</span>{t('games.memory.emptyPrompt')}</Empty>
          :<Grid $c={cfg.cols}>
            {G.cards.map(card=>{
              // faceUp = true → show front (emoji); false → show back (G letter)
              const faceUp = card.open || card.matched;
              return(
                <CardOuter
                  key={card.id}
                  $shake={G.shakeIds.includes(card.id)}
                  onClick={()=>clickCard(card.id)}
                >
                  {/* BACK: shown when faceUp===false */}
                  <CardBack $v={!faceUp}/>
                  {/* FRONT: shown when faceUp===true */}
                  <CardFront $v={faceUp} $m={card.matched}>
                    {/* key forces re-mount so emojiIn animation replays */}
                    {faceUp && <EmojiSpan key={`${card.id}-open`}>{card.emoji}</EmojiSpan>}
                  </CardFront>
                </CardOuter>
              );
            })}
          </Grid>
        }
      </BoardArea>

      {(G.phase==='won'||G.phase==='lost')&&(
        <Overlay>
          <Modal>
            <MEmo>{G.phase==='won'?'🏆':'💀'}</MEmo>
            <MTit>{G.phase==='won' ? t('games.memory.wonTitle') : t('games.memory.lostTitle')}</MTit>
            <MSub>{G.phase==='won' ? t('games.memory.wonSub', { moves: G.moves }) : t('games.memory.lostSub')}</MSub>
            {G.phase==='won'&&<MScore>{score} pts</MScore>}
            <MBtn onClick={start}>{t('games.memory.playAgain')}</MBtn>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
